/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { SubscriptionApi, Configuration } from './backend'
import { BackendConfiguration } from './backend/base'
import type {
  CreateCheckoutSessionRequest,
  SubscriptionRequest,
} from './backend/models'
import type {
  SubscriptionPlan,
  UserSubscriptionState,
  SubscriptionResponse,
  CheckoutSessionResult,
} from '@/types/subscription'
import { mapSubscriptionResponse } from '@/types/subscription'
import appConfig from '@/config/appConfig'
import { logger } from '@/services/logger'



/**
 * Service for handling subscription-related API calls
 */
export class SubscriptionService {
  private api: SubscriptionApi
  private configuration: Configuration

  constructor() {
    // Use BackendConfiguration which includes JWT Bearer authentication
    this.configuration = new Configuration({
      ...BackendConfiguration,
      basePath: appConfig.apiBaseUrl,
      baseOptions: {
        ...BackendConfiguration.baseOptions,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...BackendConfiguration.baseOptions?.headers
        }
      }
    })

    this.api = new SubscriptionApi(this.configuration)
  }



  /**
   * Get current subscription information for the user
   */
  async getSubscription(): Promise<UserSubscriptionState> {
    try {
      const response = await this.api.getSubscription()
      const data = response.data as SubscriptionResponse

      return mapSubscriptionResponse(data)
    } catch (error) {
      logger.error('Error fetching subscription:', error instanceof Error ? error : undefined, {}, 'subscriptionService')
      // Return default free subscription on error
      return {
        plan: 'free',
        status: 'active',
        usageCount: 0,
        maxFreeUsage: appConfig.subscription.maxFreeUsage,
        isPremium: false,
        isCancelled: false,
        isExpired: false,
        expiresAt: null,
        cancelledAt: null,
        startedAt: null
      }
    }
  }

  /**
   * Create a Stripe checkout session for subscription
   */
  async createCheckoutSession(plan: SubscriptionPlan): Promise<CheckoutSessionResult> {
    try {
      const request: CreateCheckoutSessionRequest = {
        plan: plan
      }

      const response = await this.api.createCheckoutSession(request)
      const data = response.data

      return {
        success: data.success || false,
        url: data.url || undefined,
        sessionId: data.sessionId || undefined,
        error: data.error || undefined
      }
    } catch (error) {
      logger.error('Error creating checkout session:', error instanceof Error ? error : undefined, {}, 'subscriptionService')
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create checkout session'
      }
    }
  }


  /**
   * Cancel the user's premium subscription
   */
  async cancelSubscription(): Promise<{ success: boolean; error?: string; expiresAt?: Date; message?: string }> {
    try {
      const response = await this.api.cancelSubscription()
      const data = response.data

      return {
        success: data.success || false,
        expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
        message: data.message || undefined,
        error: data.error || undefined
      }
    } catch (error) {
      logger.error('Error cancelling subscription:', error instanceof Error ? error : undefined, {}, 'subscriptionService')
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to cancel subscription'
      }
    }
  }

  /**
   * Update user's subscription plan
   */
  async updateSubscription(userId: string, plan: string): Promise<{ success: boolean; error?: string }> {
    try {
      const request: SubscriptionRequest = {
        userId: userId,
        plan: plan
      }

      const response = await this.api.updateSubscription(request)
      const data = response.data

      return {
        success: data.success || false,
        error: data.error || undefined
      }
    } catch (error) {
      logger.error('Error updating subscription:', error instanceof Error ? error : undefined, {}, 'subscriptionService')
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update subscription'
      }
    }
  }

  /**
   * Get subscription status (alias for getSubscription)
   */
  async getStatus(): Promise<UserSubscriptionState> {
    try {
      const response = await this.api.status()
      const data = response.data as SubscriptionResponse

      return mapSubscriptionResponse(data)
    } catch (error) {
      logger.error('Error fetching subscription status:', error instanceof Error ? error : undefined, {}, 'subscriptionService')
      // Return default free subscription on error
      return {
        plan: 'free',
        status: 'active',
        usageCount: 0,
        maxFreeUsage: appConfig.subscription.maxFreeUsage,
        isPremium: false,
        isCancelled: false,
        isExpired: false,
        expiresAt: null,
        cancelledAt: null,
        startedAt: null
      }
    }
  }

  /**
   * Check subscription status (returns new UserSubscriptionState)
   */
  async checkStatus(): Promise<UserSubscriptionState> {
    return this.getSubscription()
  }
}

/**
 * Create a subscription service instance
 */
export function createSubscriptionService(): SubscriptionService {
  return new SubscriptionService()
}

export default SubscriptionService
